import Store from 'store/store';
import 'store/member';
import 'store/customTracking';
import { apiPrime } from 'utils/bolt/apiPrime';
import { getUserTickets } from 'helpers/tickets';

const CLIENT_ID = 'amzn1.application-oa2-client.55d73ce9d7fa4b128a8e63f3ae34703e';
const TEST_EMAIL = 'uranup+primenew01@amazon.com';

const implementIdleTimeout = () => {
	const timeInMs = 10 * 60 * 1000;
	window.checkingForAmazonLoginIdle = window.setTimeout(() => location.reload(), timeInMs);
};

const clearIdleTimeout = () => window.clearTimeout(window.checkingForAmazonLoginIdle);

const getCookie = (name) => {
	let value = '; ' + document.cookie;
	let parts = value.split('; ' + name + '=');
	if (parts.length == 2) {
		return parts.pop().split(';').shift();
	}
};

export const loadSDK = async () => {
	if (document.querySelector('script[src*="assets.loginwithamazon.com"]')) {
		return;
	}
	return new Promise((resolve, reject) => {
		let script = document.createElement('script');
		script.onload = resolve;
		script.onerror = reject;
		script.async = true;
		script.id = 'amazon-login-sdk';
		script.src = 'https://assets.loginwithamazon.com/sdk/na/login1.js';
		document.body.appendChild(script);
	});
};

// Promisify amazon login functions
const [
	retrieveProfile,
	authorize,
	retrieveToken,
] = [
	'retrieveProfile',
	'authorize',
	'retrieveToken',
].map((method) => {
	return async (...args) => {
		if (!window.amazon?.Login?.[method]) {
			// Should we try loadSDK?
			return Promise.reject(new Error('Amazon Login SDK not loaded'));
		}
		return new Promise((res, rej) => {
			window.amazon.Login[method](...args, (r) => r.error ? rej(new Error(r.error)) : res(r));
		});
	};
});

export const setClientID = () => {
	window.amazon.Login.setClientId(CLIENT_ID);
	checkAlreadyAuthorised();
};

const checkAlreadyAuthorised = async () => {
	let amazonLoginCookie = getCookie('amazon_Login_state_cache');
	if (!amazonLoginCookie) return;
	amazonLoginCookie = JSON.parse(decodeURIComponent(amazonLoginCookie));
	await processProfile(amazonLoginCookie.access_token);
};

const deleteAuthCookies = () => {
	document.cookie = 'amazon_Login_state_cache=; expires=Thu, 01 Jan 1970 00:00:01 UTC; path=/';
	document.cookie = 'amazon_Login_auth_code_scope_cache=; expires=Thu, 01 Jan 1970 00:00:01 UTC; path=/';
};

const checkAccess = async (accessToken, region, profile) => {
	if (profile?.PrimaryEmail === TEST_EMAIL) {
		return true;
	}
	try {
		const res = await apiPrime({
			method: 'get',
			path: '/get-user-benefits',
			query: { user_access_token: accessToken, region_code: region }
		});

		return res?.response?.data?.includes('PRIME_POWSTER_BENEFIT');
	} catch (err) {
		console.error('Error checking prime benefit - ', err);
		return false;
	}
};

const processProfile = async (accessToken, region = __REGION__) => {
	try {
		const { profile } = await retrieveProfile(accessToken);

		if (!checkAccess(accessToken, region, profile)) {
			Store.emit('SHOW_PRIME_BLOCK', true);
			Store.emit('SHOW_FULL_LOADER', false);
			return;
		}

		const tickets = await getUserTickets(profile?.CustomerId);

		Store.emit('RETRIEVED_PROFILE', profile);
		Store.emit('RETRIEVED_USER_TICKETS', tickets);
		Store.emit('TRACK_AUTH_SUCCESS');
	} catch (err) {
		console.error('Error retrieving profile - ', err);
	}
	Store.emit('SHOW_FULL_LOADER', false);
};

export const startLogin = async (region) => {
	Store.emit('SHOW_FULL_LOADER', true);
	let amazonLoginCookie = getCookie('amazon_Login_state_cache');
	if (amazonLoginCookie) {
		deleteAuthCookies();
	}

	try {
		implementIdleTimeout();
		const { code } = await authorize({ scope: 'profile prime:benefit_status', pkce: true });
		clearIdleTimeout();
		const { access_token: accessToken } = await retrieveToken(code);
		return processProfile(accessToken, region);
	} catch (err) {
		console.error('Error during Amazon login - ', err);
		Store.emit('SHOW_FULL_LOADER', false);
	}
};

